import { ApiTypes } from "types"

export interface TrendMetric {
  title: string
  tooltip: string
  value: string
  change?: string
  direction?: "up" | "down"
  tone: "positive" | "negative" | "neutral"
  helper: string
  trend: (number | null)[]
}

const HELPER = "past 7 days"

export const describeDelta = (delta: number, higherIsBetter: boolean, unit: string) => {
  const rounded = Math.round(Math.abs(delta))

  if (rounded === 0) {
    return { change: `0${unit}`, direction: undefined, tone: "neutral" as const }
  }

  const direction = delta > 0 ? ("up" as const) : ("down" as const)
  const isGood = (direction === "up") === higherIsBetter

  return { change: `${rounded}${unit}`, direction, tone: isGood ? ("positive" as const) : ("negative" as const) }
}

const buildInstallRate = ({ current, previous, daily }: ApiTypes.Model.ReferralAgentTrendMetric): TrendMetric => ({
  title: "Install rate",
  tooltip: "Share of referrals submitted in the last 7 days that were installed.",
  value: `${Math.round(current * 100)}%`,
  helper: HELPER,
  ...describeDelta((current - previous) * 100, true, "%"),
  trend: daily.map(({ value }) => Math.round(value * 100)),
})

const buildDaysToInstall = ({ current, previous, daily }: ApiTypes.Model.ReferralAgentTrendMetric): TrendMetric => {
  const trend = daily.map(({ value }) => (value === 0 ? null : value))

  const tooltip = "Median number of days from submission to installation for referrals installed in the last 7 days."

  if (current === 0) {
    return { title: "Days to install", tooltip, value: "N/A", tone: "neutral", helper: HELPER, trend }
  }

  return {
    title: "Days to install",
    tooltip,
    value: `${current}d`,
    helper: HELPER,
    ...(previous === 0 ? { tone: "neutral" as const } : describeDelta(current - previous, false, "d")),
    trend,
  }
}

const buildFromLinkOrQr = ({ current, previous, daily }: ApiTypes.Model.ReferralAgentTrendMetric): TrendMetric => ({
  title: "From your link or QR",
  tooltip: "Number of times your referral link was opened or QR code scanned in the last 7 days.",
  value: String(current),
  helper: HELPER,
  ...(previous === 0
    ? { tone: "neutral" as const }
    : describeDelta(((current - previous) / previous) * 100, true, "%")),
  trend: daily.map(({ value }) => value),
})

export const buildTrendMetrics = (widget: ApiTypes.Model.ReferralAgentDashboardTrends): TrendMetric[] => [
  buildInstallRate(widget.install_rate),
  buildDaysToInstall(widget.days_to_install),
  buildFromLinkOrQr(widget.from_link_or_qr),
]
